<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useModalFocusTrap } from '../../composables/useModalFocusTrap';
import { fieldErrorMessage } from '../../utils/validationMessage';

/**
 * Il report non è partito perché il Gateway ZDHC non ha accettato uno o più
 * prodotti fra quelli selezionati.
 *
 * **Cosa sostituisce**: uno snackbar che diceva «alcuni prodotti sono stati
 * rifiutati: correggili e riprova (1)». Il numero c'era, i prodotti no — e
 * nemmeno il motivo: chi lo leggeva sapeva di dover correggere qualcosa, non
 * cosa.
 *
 * **Perché un dialog e non uno snackbar**: stesso criterio dei gemelli
 * `DuplicateReportLinesModal` e `NegativeQuantityModal`. Lo snackbar dura due
 * secondi in basso a sinistra, e questo messaggio nomina prodotti e va letto
 * per poter agire. Di tutti e tre è anzi quello che può portare la lista più
 * lunga: l'api spinge le righe una a una e raccoglie **tutti** i rifiuti, non
 * si ferma al primo.
 *
 * **Cosa dice che lo snackbar taceva**: quali prodotti, perché, e quanti ne
 * sono passati. Non dice invece cosa fare dopo: la frase che lo spiegava è
 * stata tolta su richiesta. Resta vero che rilanciare il report dopo la
 * correzione non duplica niente — la sincronizzazione è un diff idempotente
 * (`ZdhcInventoryService::syncLines`) — ma il dialog non lo racconta più, e chi
 * volesse rimetterlo ha qui il perché era stato scritto.
 *
 * **Da dove viene il numero degli accettati.** Per differenza fra le righe
 * inviate e quelle rifiutate, mai da `lines_added` + `lines_updated`: quei due
 * contatori non contano le righe già presenti e identiche, che a un secondo
 * tentativo restano intatte senza incrementare nessuno dei due. Sarebbero un
 * numero giusto al primo invio e falso al riprova, cioè esattamente quando lo
 * si legge. La differenza è invece esatta sempre, perché una riga che non è
 * fra le rifiutate è stata accettata — che sia stata aggiunta ora, aggiornata,
 * o trovata già identica.
 *
 * Nomi e messaggi interpolati come testo e non con `v-html`: i primi li scrive
 * l'utente, i secondi arrivano dal Gateway.
 */

/* Emits */
const emits = defineEmits(['closeModal']);
/* Props */
const props = defineProps({
    /*
     * Le righe già risolte da chi apre il dialog:
     * `{ name, referenceId, productName, formulatorName, reasons }`.
     * Gli indici di riga li ha tradotti lui, così qui non entra aritmetica
     * sugli array del payload.
     */
    lines: { required: true, type: Array },
    /*
     * Quante righe sono state inviate in tutto: gli accettati sono questo meno
     * le rifiutate, e non c'è un altro modo di saperli che sia esatto.
     */
    total: { required: true, type: Number }
});
/* Data */
const { t } = useI18n();
const store = useStore();
const dialog = ref(null);
/* Computed */
/**
 * Quante righe il Gateway ha accettato. Mai negativo: un totale più piccolo
 * delle righe rifiutate non è una situazione prevista, ma un numero sotto zero
 * dentro una frase sarebbe assurdo a schermo.
 */
const accepted = computed( () => Math.max(props.total - props.lines.length, 0));
/**
 * La frase d'apertura, con la forma che il numero richiede.
 *
 * Tre chiavi e non una: «1 prodotti sono stati accettati» e «0 prodotti sono
 * stati accettati» sono entrambe sbagliate in italiano, e qui non c'è
 * pluralizzazione configurata su cui appoggiarsi. Zero è raggiungibile — il
 * Gateway può rifiutare tutte le righe — quindi non è un caso teorico.
 *
 * Il numero è interpolato con `replace` e non con i parametri di `t`: qui `t`
 * viene dal `useI18n()` e la sua terza posizione non è quella del `$t` globale.
 * È la stessa convenzione di `newInventoryModal.limitErrorTitle`.
 */
const intro = computed( () =>
{
    const key = accepted.value === 0 ? 'introNone' : accepted.value === 1 ? 'introOne' : 'intro';

    return t(`rejectedReportLinesModal.${key}`, store.state.locale).replace('__accepted', String(accepted.value));
});
/**
 * Una riga per prodotto: come si chiama, con che identità è partito, e perché è
 * stato rifiutato.
 *
 * Un nome mancante non lascia un buco nella frase — `products.name` è nullable
 * e un prodotto senza nome resta comunque una riga del report.
 */
const rows = computed( () => props.lines.map( (line) =>
{
    const name = typeof line.name === 'string' && line.name.trim() !== ''
        ? line.name.trim()
        : t('rejectedReportLinesModal.unnamedProduct', store.state.locale);

    const identity = line.referenceId
        ? `${t('productTable.cols.reference_id', store.state.locale)} ${line.referenceId}`
        : `${line.productName ?? '—'} / ${line.formulatorName ?? '—'}`;

    return { name, identity, reasons: (line.reasons ?? []).map(reasonOf) };
}));
/* Methods */
/**
 * Il motivo di un campo, in una riga sola.
 *
 * La sceglie `fieldErrorMessage`, lo stesso modulo che veste gli errori di
 * validazione sotto i campi dei form — la mappa `validation.fields` è per campo
 * e vale per tutta l'applicazione, e `product_reference_id`, che è il rifiuto
 * di gran lunga più frequente, c'è già.
 *
 * Per un campo che non ha traduzione resta il messaggio del Gateway, in
 * inglese: è la ricaduta di `fieldErrorMessage`, e senza di essa un rifiuto che
 * non prevediamo non direbbe niente. Senza nemmeno un messaggio resta il nome
 * del campo: grezzo, ma dice su cosa si deve guardare.
 */
const reasonOf = (reason) =>
{
    const messages = Array.isArray(reason?.messages) ? reason.messages : [];

    // L'oggetto si costruisce qui perche' `fieldErrorMessage` legge la mappa
    // `{campo: [messaggi]}` dell'api, e noi siamo gia' su un campo solo.
    return fieldErrorMessage(
        reason?.field,
        { [reason?.field]: messages },
        (key) => t(key, store.state.locale)
    ) ?? reason?.field ?? '';
};
const close = () => emits('closeModal');
/* Hooks */
useModalFocusTrap(dialog, close);
</script>
<template>
    <div
        @click.prevent="close"
        class="fixed inset-0 p-6 bg-gray-1000 bg-opacity-90 z-40 overscroll-y-contain overflow-y-auto text-blue-500">
        <!-- `min-h-full` e non `h-full`: con un'altezza fissa, un dialog più
             alto del viewport viene centrato traboccando **da entrambi i
             lati**, e la parte sopra il bordo superiore non si raggiunge —
             `scrollTop` non scende sotto zero. Con `min-h-full` il contenitore
             cresce col contenuto e il centraggio diventa inerte. -->
        <div class="flex flex-col w-full min-h-full items-center justify-center">
            <div
                @click.stop
                ref="dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="rejected-report-lines-title"
                aria-describedby="rejected-report-lines-intro"
                class="bg-white dark:bg-gray-900 dark:text-gray-400 border dark:border-gray-700 rounded p-5 flex flex-col space-y-4 w-full md:w-2/3 lg:w-1/2 2xl:w-1/3">
                <div class="flex items-start justify-between space-x-3">
                    <h2 id="rejected-report-lines-title" class="text-xl flex items-center space-x-2 text-red-700 dark:text-red-300">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 fill-current flex-shrink-0" aria-hidden="true" focusable="false">
                            <path d="M12,2A10,10,0,1,0,22,12,10.011,10.011,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.009,8.009,0,0,1,12,20Zm.5-3.5a.75.75,0,1,1-.75-.75A.75.75,0,0,1,12.5,16.5ZM11.75,6.5a3,3,0,0,0-3,3,1,1,0,0,0,2,0,1,1,0,1,1,1,1,1,1,0,0,0-1,1v1.25a1,1,0,0,0,2,0v-.42a3,3,0,0,0-1-5.83Z"/>
                        </svg>
                        <span>{{ t('rejectedReportLinesModal.title', store.state.locale) }}</span>
                    </h2>
                    <button
                        :title="t('actions.close', store.state.locale)"
                        :aria-label="t('actions.close', store.state.locale)"
                        @click.prevent="close"
                        class="bg-transparent min-w-min flex items-center justify-center transform hover:rotate-90 duration-300 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" class="w-4 fill-current text-gray-800 dark:text-gray-400" aria-hidden="true" focusable="false">
                                <path d="M34.5,32L62.2,4.2c0.7-0.7,0.7-1.8,0-2.5c-0.7-0.7-1.8-0.7-2.5,0L32,29.5L4.2,1.8c-0.7-0.7-1.8-0.7-2.5,0
                                    c-0.7,0.7-0.7,1.8,0,2.5L29.5,32L1.8,59.8c-0.7,0.7-0.7,1.8,0,2.5c0.3,0.3,0.8,0.5,1.2,0.5s0.9-0.2,1.2-0.5L32,34.5l27.7,27.8
                                    c0.3,0.3,0.8,0.5,1.2,0.5c0.4,0,0.9-0.2,1.2-0.5c0.7-0.7,0.7-1.8,0-2.5L34.5,32z"/>
                            </svg>
                    </button>
                </div>

                <p id="rejected-report-lines-intro" class="text-sm">
                    {{ intro }}
                </p>

                <!-- Un prodotto per riga, con l'identità con cui è partito e il
                     motivo del rifiuto. Scorre da sola se sono molti, senza
                     allungare il dialog oltre lo schermo.

                     `max-h-60` e non `max-h-64`: questa configurazione
                     ridefinisce `maxHeight` **fuori** da `extend`, quindi
                     sostituisce la scala di default, e `64` non c'è. La classe
                     non genera CSS — non è un valore sbagliato, è una regola
                     che non esiste — e senza tetto `overflow-y-auto` non ha
                     niente da limitare.

                     `tabindex="0"` perché una regione che scorre deve poter
                     scorrere anche da tastiera: qui dentro non c'è niente di
                     focalizzabile — solo testo — quindi senza questo il Tab la
                     salta, e con lei le righe oltre il tetto. Serve anche al
                     focus trap, che enumera i focalizzabili per attributo.
                     Focalizzabile vuol dire annunciabile, da cui l'etichetta. -->
                <ul
                    tabindex="0"
                    :aria-label="t('rejectedReportLinesModal.listLabel', store.state.locale)"
                    class="flex flex-col space-y-2 max-h-60 overflow-y-auto overscroll-contain rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-300">
                    <li
                        v-for="(row, index) in rows"
                        :key="index"
                        class="text-sm border rounded-md p-3 bg-gray-100 dark:bg-gray-800 dark:border-gray-700">
                        <span class="font-semibold">{{ row.name }}</span>
                        <span class="block pt-1">{{ row.identity }}</span>
                        <ul v-if="row.reasons.length" class="flex flex-col space-y-1 pt-2">
                            <li
                                v-for="(reason, reasonIndex) in row.reasons"
                                :key="reasonIndex">
                                {{ reason }}
                            </li>
                        </ul>
                    </li>
                </ul>

                <div class="flex items-center justify-end">
                    <button
                        @click.prevent="close"
                        class="px-5 py-3 font-medium leading-5 text-center text-white bg-blue-500 hover:bg-blue-300 dark:hover:bg-blue-700 rounded duration-300 lg:w-auto focus:ring-2 focus:ring-blue-700 focus:ring-offset-2 dark:focus:ring-blue-300 dark:focus:ring-offset-gray-900">
                        {{ t('actions.close', store.state.locale) }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
